/**
 * Google Calendar v3 connector. Pulls recurring birthdays + anniversaries
 * (from the auto-created "Birthdays" calendar) plus user events.
 *
 * Dial uses these for relationship-graph nudges + calm tile.
 */
import type { CalendarEvent } from './types.ts';
import { MOCK_CALENDAR_EVENTS } from './mock-data.ts';
import { ensureFreshToken, gmailFetch, type OAuthToken, GmailOAuth } from './gmail-oauth.ts';

const LIST_URL = 'https://www.googleapis.com/calendar/v3/calendars/primary/events';
const BDAY_CAL = 'https://www.googleapis.com/calendar/v3/calendars/addressbook%23contacts%40group.v.calendar.google.com/events';

interface GEvent {
  id: string;
  summary?: string;
  start?: { date?: string; dateTime?: string };
  end?: { date?: string; dateTime?: string };
  attendees?: ReadonlyArray<{ email: string }>;
  recurrence?: readonly string[];
  description?: string;
}
interface GEventResp {
  items?: GEvent[];
  nextPageToken?: string;
}

export interface CalendarOpts {
  readonly mode: 'mock' | 'live';
  readonly oauth?: GmailOAuth;
  readonly token?: OAuthToken;
  readonly time_min?: string;
  readonly time_max?: string;
  readonly include_birthdays?: boolean;
  readonly max_results?: number;
}

export async function fetchEvents(opts: CalendarOpts): Promise<readonly CalendarEvent[]> {
  if (opts.mode === 'mock') return MOCK_CALENDAR_EVENTS;
  if (!opts.oauth || !opts.token) throw new Error('live mode requires oauth + token');

  const token = await ensureFreshToken(opts.oauth, opts.token);
  const time_min = opts.time_min ?? new Date(Date.now() - 30 * 86_400_000).toISOString();
  const time_max = opts.time_max ?? new Date(Date.now() + 540 * 86_400_000).toISOString();
  const limit = opts.max_results ?? 250;

  const out: CalendarEvent[] = [];
  out.push(...(await fetchOneCalendar(token, LIST_URL, time_min, time_max, limit)).map((e) => toEvent(e, false)));
  if (opts.include_birthdays !== false) {
    out.push(...(await fetchOneCalendar(token, BDAY_CAL, time_min, time_max, limit)).map((e) => toEvent(e, true)));
  }
  return out;
}

async function fetchOneCalendar(token: OAuthToken, url: string, time_min: string, time_max: string, limit: number): Promise<GEvent[]> {
  const items: GEvent[] = [];
  let pageToken: string | undefined;
  while (items.length < limit) {
    const u = new URL(url);
    u.searchParams.set('timeMin', time_min);
    u.searchParams.set('timeMax', time_max);
    u.searchParams.set('singleEvents', 'false');
    u.searchParams.set('orderBy', 'startTime');
    u.searchParams.set('maxResults', String(Math.min(100, limit - items.length)));
    if (pageToken) u.searchParams.set('pageToken', pageToken);
    try {
      const r = await gmailFetch<GEventResp>(token, u.toString());
      items.push(...(r.items ?? []));
      pageToken = r.nextPageToken;
      if (!pageToken) break;
    } catch {
      break;
    }
  }
  return items;
}

function toEvent(e: GEvent, force_birthday = false): CalendarEvent {
  const start = e.start?.dateTime ?? e.start?.date ?? '';
  const title = e.summary ?? '(no title)';
  const isBirthday = force_birthday || /\bbirthday|जन्मदिन|janmadin\b/i.test(title);
  const isAnniv = /\banniversary|salgirah\b/i.test(title);
  return {
    external_id: e.id,
    title,
    start,
    ...(e.end?.dateTime ? { end: e.end.dateTime } : {}),
    ...(e.recurrence?.length ? { recurrence: 'yearly' as const } : {}),
    kind: isBirthday ? 'birthday' : isAnniv ? 'anniversary' : 'event',
    ...(e.attendees?.length ? { attendees: e.attendees.map((a) => a.email) } : {}),
    provenance: { connector: 'gcalendar', source_id: e.id, fetched_at: Date.now() },
  };
}
